import { Injectable, InjectionToken } from '@angular/core';
import { CalendarOptions } from '@fullcalendar/core';
import { EventImpl, VerboseFormattingArg } from '@fullcalendar/core/internal';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimeGrid from '@fullcalendar/resource-timegrid';
import moment from 'moment/moment';
import { environment } from '../../../../environments/environment';
import { AbstractCalendarService } from '../../../core/services/AbstractCalendarService';

export const SOURCE_CALENDAR_SERVICE = new InjectionToken<CopyWeekAdvancedCalendarService>('SOURCE_CALENDAR_SERVICE');
export const TARGET_CALENDAR_SERVICE = new InjectionToken<CopyWeekAdvancedCalendarService>('TARGET_CALENDAR_SERVICE');

@Injectable()
export class CopyWeekAdvancedCalendarService extends AbstractCalendarService {

  public calendarOptions: CalendarOptions;

  protected override readonly unselectElementsSelector = 'fc-timegrid-slot';

  public constructor() {
    super();
  }

  public override getSelectedEvents(): EventImpl[] {
    return this.fullCalendarApi?.getEvents().filter(e => e.extendedProps['isSelected']) ?? [];
  }

  public buildCalendarOptions(
    initialDate: moment.Moment,
    resources: { id: string; title: string }[],
    callbacks?: { eventClick?: (arg: any) => void; eventDrop?: (arg: any) => void; eventDragStart?: (arg: any) => void },
  ): void {
    const options: CalendarOptions = {
      plugins: [resourceTimeGrid, interactionPlugin],
      schedulerLicenseKey: environment.fullcalendarLicenseKey,
      initialView: 'timeGridSevenDays',
      initialDate: initialDate.isValid() ? initialDate.format() : moment().format(),
      allDaySlot: false,
      datesAboveResources: true,
      headerToolbar: false,
      footerToolbar: false,
      slotDuration: '00:05:00',
      slotLabelFormat: (arg: VerboseFormattingArg) => moment(arg.start).format('HH:mm'),
      dayHeaderDidMount: (dayHeader): void => {
        const date = moment(dayHeader.date);
        dayHeader.el.firstElementChild.innerHTML = `
          <div class="d-flex align-items-center white-space-nowrap">
            <div class="half-width">
              <h4>${date.format('dd')}.</h4>
            </div>
            <div class="half-width d-flex align-items-center gap-8">
              <p class="p3">${date.format('DD.MM')}</p>
            </div>
          </div>
        `;
      },
      slotMinTime: '08:00:00',
      slotMaxTime: '32:00:00',
      slotLabelInterval: { hours: 1 },
      editable: false,
      eventDurationEditable: false,
      eventResourceEditable: false,
      selectable: false,
      locale: this.locales[this.languageStoreService.language.token].default,
      views: {
        timeGridSevenDays: {
          type: 'resourceTimeGrid',
          duration: { days: 7 },
        },
      },
      resources,
      events: [],
    };

    if (callbacks?.eventClick) {
      options.eventClick = callbacks.eventClick;
    }

    if (callbacks?.eventDrop) {
      options.eventDrop = callbacks.eventDrop;
    }

    if (callbacks?.eventDragStart) {
      options.eventDragStart = callbacks.eventDragStart;
    }

    this.calendarOptions = options;
  }
}
